// Arena trail steps (protocol §5.4 ARENA_TRAILS): a trail is a 4-connected cell path (the sim's
// supercover rasterisation), so after its first cell every cell is one 2-bit step from the last:
// ARENA_STEP RIGHT 0 (+x), DOWN 1 (+y), LEFT 2 (−x), UP 3 (−y). The server turns cell indices into
// steps (`trailStepCode`, `trailStepsInto`); the client turns them back (`applyTrailSteps`).
//
// Cells are indices `cy · cols + cx` (arena/grid.ts). A pair of cells that are not 4-neighbours is
// a gap (−1): it cannot be sent as a step, and the server falls back to a RESET from the cell
// after the last gap (arena/net/frame-builder.ts).
//
// Pure. Owner: lane P.

import { ARENA_STEP } from '../protocol/opcodes.js';

/** The step code from cell `from` to its 4-neighbour `to`, or −1 if they are not neighbours. */
export function trailStepCode(from: number, to: number, cols: number): number {
  const d = to - from;
  if (d === cols) return ARENA_STEP.DOWN;
  if (d === -cols) return ARENA_STEP.UP;
  if (d === 1) return from % cols === cols - 1 ? -1 : ARENA_STEP.RIGHT;
  if (d === -1) return from % cols === 0 ? -1 : ARENA_STEP.LEFT;
  return -1;
}

/** The cell one step `code` (ARENA_STEP) from `cell`. No bounds check: the sender guarantees it. */
export function stepCell(cell: number, code: number, cols: number): number {
  switch (code & 3) {
    case ARENA_STEP.RIGHT:
      return cell + 1;
    case ARENA_STEP.DOWN:
      return cell + cols;
    case ARENA_STEP.LEFT:
      return cell - 1;
    default:
      return cell - cols;
  }
}

/**
 * Appends the steps `cells[from] → cells[from+1] → … → cells[to]` to `out` (to − from steps).
 * Returns the index of the last gap in that range (the pair `cells[i−1], cells[i]` that are not
 * neighbours, reported as `i`), or −1 when the whole range is 4-connected. On a gap, `out` holds
 * the steps written up to it; the caller discards them.
 */
export function trailStepsInto(
  cells: ArrayLike<number>,
  from: number,
  to: number,
  cols: number,
  out: number[],
): number {
  let gap = -1;
  for (let i = from + 1; i <= to; i++) {
    const code = trailStepCode(cells[i - 1] as number, cells[i] as number, cols);
    if (code < 0) gap = i;
    else if (gap < 0) out.push(code);
  }
  return gap;
}

/**
 * The client side: appends the cells reached by `steps` from `start` (exclusive) to `out` and
 * returns the last cell (`start` when there are no steps).
 */
export function applyTrailSteps(
  start: number,
  steps: ArrayLike<number>,
  cols: number,
  out: number[],
): number {
  let c = start;
  for (let i = 0; i < steps.length; i++) {
    c = stepCell(c, steps[i] as number, cols);
    out.push(c);
  }
  return c;
}
